import { useState, useEffect, useMemo, Fragment } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { DatePickerInput } from '../components/ui/DatePickerInput';
import {
  DollarSign,
  Calendar,
  User,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  TrendingUp,
  TrendingDown,
  Package,
  ChevronDown,
  ChevronUp,
  Banknote,
  CreditCard,
  Tag,
  Smartphone,
  X,
  Check,
  Trash2,
  Pencil,
} from 'lucide-react';
import { CashRegisterService, type ClosureItem, type UpdateCierrePayload } from '../services/cash-register/cash-register.service';
import { OrdersService } from '../services/orders/orders.service';
import { UsersService } from '../services/users/users.service';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';

const formatPrice = (value: number) => {
  if (value === 0) return '$0';
  return `$${Math.round(value).toLocaleString('es-CO')}`;
};

const formatDate = (iso: string) => {
  const datePart = iso.slice(0, 10);
  const [yyyy, mm, dd] = datePart.split('-');
  return `${dd}/${mm}/${yyyy}`;
};

const calcularMargenPorcentaje = (ventas: number, costo: number) => {
  if (ventas <= 0) return 0;
  return ((ventas - costo) / ventas) * 100;
};

/* ─── Detalle expandible de un cierre ─── */
function ClosureDetailRow({ cierre, admin, onEdit }: { cierre: ClosureItem; admin?: boolean; onEdit?: (c: ClosureItem) => void }) {
  const queryClient = useQueryClient();
  const [loadingVentas, setLoadingVentas] = useState(false);
  const [ventas, setVentas] = useState<any[]>([]);
  const [mostrarProductos, setMostrarProductos] = useState(false);

  const cargarVentas = async () => {
    if (ventas.length > 0) return;
    setLoadingVentas(true);
    try {
      const fecha = cierre.fecha.slice(0, 10);
      const desde = `${fecha}T05:00:00.000Z`;
      const hasta = new Date(`${fecha}T05:00:00.000Z`);
      hasta.setDate(hasta.getDate() + 1);
      hasta.setMilliseconds(hasta.getMilliseconds() - 1);
      const params: any = { desde, hasta: hasta.toISOString(), limit: 500 };
      params.vendedorId = cierre.vendedorId || cierre.usuarioId;
      const response = await OrdersService.getMySales(params);
      if (response.success && response.data) {
        setVentas(response.data.filter((v: any) => v.estado !== 'CANCELLED'));
      }
    } catch {
      // Silencioso
    } finally {
      setLoadingVentas(false);
    }
  };

  const metodos = [
    { key: 'efectivo', label: 'Efectivo', ventas: cierre.efectivoVentas, contado: cierre.efectivoContado, icon: Banknote },
    { key: 'tarjeta', label: 'Tarjeta', ventas: cierre.tarjetaVentas, contado: cierre.tarjetaContado, icon: CreditCard },
    { key: 'transferencias', label: 'Transferencias', ventas: cierre.transferenciasVentas, contado: cierre.transferenciasContado, icon: Tag },
    { key: 'nequi', label: 'Nequi / Daviplata', ventas: cierre.nequiVentas, contado: cierre.nequiContado, icon: Smartphone },
  ];

  const costoProductos = ventas.reduce((sum, v) => {
    const itemsCosto = (v.items || []).reduce((s: number, item: any) => {
      const pc = item.precioCompra || 0;
      return s + pc * (item.cantidad || 0);
    }, 0);
    return sum + itemsCosto;
  }, 0);

  return (
    <tr>
      <td colSpan={9} className="px-4 py-4 bg-gray-50/80 border-b border-gray-200">
      <div className="space-y-4">
        {/* Header del acordeón con acciones */}
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">Detalle del cierre</h4>
          {admin && (
            <div className="flex items-center gap-2">
              {onEdit && (
                <button
                  onClick={() => onEdit(cierre)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Ajustar cierre
                </button>
              )}
              <button
                onClick={() => {
                  if (confirm('¿Está seguro de eliminar este cierre de caja?')) {
                    CashRegisterService.deleteClosure(cierre.id).then(() => {
                      queryClient.invalidateQueries({ queryKey: ['closures'] });
                      toast.success('Cierre eliminado');
                    }).catch(() => {
                      toast.error('Error al eliminar el cierre');
                    });
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Eliminar cierre
              </button>
            </div>
          )}
        </div>

        {/* Tarjetas resumen del cierre */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg border border-gray-200 bg-white">
            <p className="text-[10px] uppercase tracking-wide text-gray-500">Ventas del Día</p>
            <p className="text-lg font-bold text-emerald-600">{formatPrice(cierre.totalVentas)}</p>
          </div>
          <div className="p-3 rounded-lg border border-gray-200 bg-white">
            <p className="text-[10px] uppercase tracking-wide text-gray-500">Dinero Contado</p>
            <p className="text-lg font-bold text-blue-600">{formatPrice(cierre.totalContado)}</p>
          </div>
          <div className="p-3 rounded-lg border border-gray-200 bg-white">
            <p className="text-[10px] uppercase tracking-wide text-gray-500">Diferencia</p>
            <p className={`text-lg font-bold ${
              cierre.diferenciaTotal === 0 ? 'text-emerald-600' : cierre.diferenciaTotal > 0 ? 'text-blue-600' : 'text-red-600'
            }`}>
              {cierre.diferenciaTotal === 0 ? 'Cuadre' : formatPrice(cierre.diferenciaTotal)}
            </p>
          </div>
          <div className="p-3 rounded-lg border border-gray-200 bg-white">
            <p className="text-[10px] uppercase tracking-wide text-gray-500">Margen Bruto</p>
            <p className="text-lg font-bold text-purple-600">{formatPrice(cierre.margenBruto)}</p>
          </div>
        </div>

        {/* Desglose por método de pago */}
        <div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Método</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Ventas</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Contado</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Diferencia</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {metodos.map((m) => {
                const dif = m.contado - m.ventas;
                return (
                  <tr key={m.key}>
                    <td className="px-3 py-2 font-medium text-gray-900 flex items-center gap-1.5">
                      <m.icon className="w-3.5 h-3.5 text-gray-400" />
                      {m.label}
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-600 font-medium">{formatPrice(m.ventas)}</td>
                    <td className="px-3 py-2 text-right text-blue-600 font-medium">{formatPrice(m.contado)}</td>
                    <td className={`px-3 py-2 text-right font-bold ${
                      dif === 0 ? 'text-emerald-600' : dif > 0 ? 'text-blue-600' : 'text-red-600'
                    }`}>
                      {dif === 0 ? '—' : formatPrice(dif)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Productos vendidos */}
        <div>
          <button
            onClick={() => {
              setMostrarProductos(!mostrarProductos);
              if (!mostrarProductos) cargarVentas();
            }}
            className="flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
          >
            <Package className="w-4 h-4" />
            {mostrarProductos ? 'Ocultar productos vendidos' : 'Ver productos vendidos'}
            {mostrarProductos ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {mostrarProductos && (
            <div className="mt-3 rounded-lg border border-gray-200 overflow-hidden bg-white">
              {loadingVentas ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                </div>
              ) : ventas.length === 0 ? (
                <div className="py-4 text-center text-sm text-gray-500">No hay ventas para este día.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Pedido</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Producto</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Cant</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Precio</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Costo</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Margen</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">% Margen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {ventas.flatMap((v) =>
                      (v.items || []).map((item: any, idx: number) => {
                        const costo = (item.precioCompra || 0) * (item.cantidad || 0);
                        const margenItem = item.subtotal - costo;
                        return (
                          <tr key={`${v.id}-${idx}`}>
                            <td className="px-3 py-2 font-mono text-emerald-600">{v.numeroPedido}</td>
                            <td className="px-3 py-2 text-gray-900">
                              {item.nombreProducto}
                              {item.nombrePresentacion && (
                                <span className="text-gray-500 ml-1">({item.nombrePresentacion})</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">{item.cantidad}</td>
                            <td className="px-3 py-2 text-right font-medium">{formatPrice(item.subtotal)}</td>
                            <td className="px-3 py-2 text-right text-red-500">{formatPrice(costo)}</td>
                            <td className="px-3 py-2 text-right font-bold text-blue-600">{formatPrice(margenItem)}</td>
                            <td className="px-3 py-2 text-right text-purple-600 font-semibold">
                              {item.subtotal > 0 ? `${((item.subtotal - costo) / item.subtotal * 100).toFixed(1)}%` : '—'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50 font-bold">
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-right text-gray-700">Totales</td>
                      <td className="px-3 py-2 text-right text-emerald-600">
                        {formatPrice(ventas.reduce((s, v) => s + (v.totales?.total || 0), 0))}
                      </td>
                      <td className="px-3 py-2 text-right text-red-500">{formatPrice(costoProductos)}</td>
                      <td className="px-3 py-2 text-right text-blue-600">
                        {formatPrice(ventas.reduce((s, v) => s + (v.totales?.total || 0), 0) - costoProductos)}
                      </td>
                      <td className="px-3 py-2 text-right text-purple-600 font-bold">
                        {(() => {
                          const totalV = ventas.reduce((s, v) => s + (v.totales?.total || 0), 0);
                          return totalV > 0 ? `${((totalV - costoProductos) / totalV * 100).toFixed(1)}%` : '—';
                        })()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
      </td>
    </tr>
  );
}

export default function CajaPage() {
  const { isAdmin } = usePermissions();
  const admin = isAdmin();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [vendedorId, setVendedorId] = useState('');
  const [vendedores, setVendedores] = useState<Array<{ id: string; nombreCompleto: string }>>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const cargarVendedores = async () => {
    try {
      const result = await UsersService.getUsers(1, 500);
      if (result.data) {
        setVendedores(result.data.map((u: any) => ({ id: u.id, nombreCompleto: u.nombreCompleto })));
      }
    } catch {
      // Silencioso
    }
  };

  useEffect(() => {
    if (admin) cargarVendedores();
  }, [admin]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['closures', page, limit, desde, hasta, vendedorId],
    queryFn: () =>
      CashRegisterService.getClosures({
        page,
        limit,
        desde: desde || undefined,
        hasta: hasta || undefined,
        vendedorId: vendedorId || undefined,
      }),
    placeholderData: (previousData) => previousData,
  });

  const closures = data?.data || [];
  const meta = data?.meta;

  const resumen = useMemo(() => {
    const totalVentas = closures.reduce((sum, c) => sum + (c.totalVentas || 0), 0);
    const totalCosto = closures.reduce((sum, c) => sum + (c.costoTotal || 0), 0);
    const margenBruto = totalVentas - totalCosto;
    const porcentaje = calcularMargenPorcentaje(totalVentas, totalCosto);
    const totalVentasCount = closures.reduce((sum, c) => sum + (c.cantidadVentas || 0), 0);
    return { totalVentas, totalCosto, margenBruto, porcentaje, totalVentasCount };
  }, [closures]);

  const clearFilters = () => {
    setDesde('');
    setHasta('');
    setVendedorId('');
    setPage(1);
    setExpandedId(null);
  };

  const handlePageChange = (newPage: number) => { setPage(newPage); };
  const handlePreviousPage = () => { if (meta && page > 1) setPage(page - 1); };
  const handleNextPage = () => { if (meta && page < meta.totalPages) setPage(page + 1); };
  const handleLimitChange = (newLimit: number) => { setLimit(newLimit); setPage(1); };

  // ─── Estados para edición de cierre ───
  const [editingClosure, setEditingClosure] = useState<ClosureItem | null>(null);
  const [editForm, setEditForm] = useState<UpdateCierrePayload>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const openEditModal = (cierre: ClosureItem) => {
    setEditingClosure(cierre);
    setEditForm({
      efectivoContado: cierre.efectivoContado,
      tarjetaContado: cierre.tarjetaContado,
      transferenciasContado: cierre.transferenciasContado,
      nequiContado: cierre.nequiContado,
    });
  };

  const closeEditModal = () => {
    setEditingClosure(null);
    setEditForm({});
    setIsSavingEdit(false);
  };

  const handleSaveEdit = async () => {
    if (!editingClosure) return;
    setIsSavingEdit(true);
    try {
      const payload: UpdateCierrePayload = {
        efectivoContado: editForm.efectivoContado,
        tarjetaContado: editForm.tarjetaContado,
        transferenciasContado: editForm.transferenciasContado,
        nequiContado: editForm.nequiContado,
      };
      await CashRegisterService.updateClosure(editingClosure.id, payload);
      queryClient.invalidateQueries({ queryKey: ['closures'] });
      toast.success('Cierre actualizado correctamente');
      closeEditModal();
    } catch {
      toast.error('Error al actualizar el cierre');
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-80px)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Cierres de Caja</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {admin && (
              <Listbox value={vendedorId} onChange={(val) => { setVendedorId(val); setPage(1); setExpandedId(null); }}>
                <div className="relative">
                  <ListboxButton className="relative w-[200px] cursor-pointer rounded-xl border border-gray-200 bg-white py-2.5 pl-3.5 pr-10 text-left text-sm text-gray-900 shadow-sm hover:border-emerald-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
                    <span className="block truncate">
                      {vendedorId ? vendedores.find((v) => v.id === vendedorId)?.nombreCompleto : 'Todos los vendedores'}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    </span>
                  </ListboxButton>
                  <ListboxOptions
                    transition
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <ListboxOption
                      value=""
                      className="group relative cursor-pointer select-none py-2.5 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                    >
                      <span className="block truncate">Todos los vendedores</span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                        <Check className="h-4 w-4" />
                      </span>
                    </ListboxOption>
                    {vendedores.map((v) => (
                      <ListboxOption
                        key={v.id}
                        value={v.id}
                        className="group relative cursor-pointer select-none py-2.5 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                      >
                        <span className="block truncate">{v.nombreCompleto}</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                          <Check className="h-4 w-4" />
                        </span>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            )}

            {/* Fecha Desde */}
            <DatePickerInput
              value={desde}
              onChange={(val) => { setDesde(val); setPage(1); setExpandedId(null); }}
              placeholder="Desde"
            />

            {/* Fecha Hasta */}
            <DatePickerInput
              value={hasta}
              onChange={(val) => { setHasta(val); setPage(1); setExpandedId(null); }}
              placeholder="Hasta"
            />
            {(desde || hasta || vendedorId) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Resumen */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="p-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs uppercase tracking-wide text-gray-500">Total Ventas</p>
            <p className="text-2xl font-bold text-emerald-600">{formatPrice(resumen.totalVentas)}</p>
          </div>
          <div className="p-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs uppercase tracking-wide text-gray-500">Costo Productos</p>
            <p className="text-2xl font-bold text-red-500">{formatPrice(resumen.totalCosto)}</p>
          </div>
          <div className="p-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs uppercase tracking-wide text-gray-500">Margen Bruto</p>
            <p className="text-2xl font-bold text-blue-600">{formatPrice(resumen.margenBruto)}</p>
          </div>
          <div className="p-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs uppercase tracking-wide text-gray-500">% Margen</p>
            <p className="text-2xl font-bold text-purple-600">{resumen.porcentaje.toFixed(1)}%</p>
          </div>
          <div className="p-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs uppercase tracking-wide text-gray-500">Cantidad Ventas</p>
            <p className="text-2xl font-bold text-gray-700">{resumen.totalVentasCount}</p>
          </div>
        </div>
      </div>

      {/* Tabla con acordeón */}
      <div className="relative flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : closures.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Package className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">No hay cierres de caja registrados.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-3 w-8"></th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Fecha</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Vendedor</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Ventas</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Costo</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Margen Bruto</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">% Margen</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700"># Ventas</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Diferencia</th>
              </tr>
            </thead>
            <tbody>
              {closures.map((c) => {
                const margenPct = calcularMargenPorcentaje(c.totalVentas, c.costoTotal);
                const isExpanded = expandedId === c.id;
                return (
                  <Fragment key={c.id}>
                    <tr
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : c.id)}
                    >
                      <td className="px-2 py-3">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{formatDate(c.fecha)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900">{c.usuario?.nombreCompleto || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-600">
                        {formatPrice(c.totalVentas)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-red-500">
                        {formatPrice(c.costoTotal)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-blue-600">
                        {formatPrice(c.margenBruto)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md ${
                          margenPct >= 30
                            ? 'bg-emerald-100 text-emerald-700'
                            : margenPct >= 15
                            ? 'bg-blue-100 text-blue-700'
                            : margenPct >= 0
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {margenPct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {margenPct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-700">
                        {c.cantidadVentas}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs font-semibold ${
                          c.diferenciaTotal === 0
                            ? 'text-emerald-600'
                            : c.diferenciaTotal > 0
                            ? 'text-blue-600'
                            : 'text-red-600'
                        }`}>
                          {c.diferenciaTotal === 0 ? 'Cuadre' : formatPrice(c.diferenciaTotal)}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <ClosureDetailRow cierre={c} admin={admin} onEdit={openEditModal} />
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {meta && meta.total > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">
                  {((meta.page - 1) * meta.limit) + 1}
                </span>
                <span className="mx-1">–</span>
                <span className="font-medium text-gray-900">
                  {Math.min(meta.page * meta.limit, meta.total)}
                </span>
                <span className="mx-2">de</span>
                <span className="font-semibold text-gray-900">{meta.total}</span>
                <span className="ml-1">resultados</span>
              </div>
              <div className="text-sm text-gray-500">
                Página <span className="font-medium text-gray-900">{meta.page}</span> de <span className="font-medium text-gray-900">{meta.totalPages}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Selector de items por página */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Mostrar</span>
                <select
                  value={limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                  className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                >
                  {[10, 20, 30, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              {/* Controles de navegación */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={page === 1}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                    page === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Primera página"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={handlePreviousPage}
                  disabled={page === 1}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                    page === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Página anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-1 mx-1">
                  {(() => {
                    const pages = [];
                    const maxVisible = 3;
                    let start = Math.max(1, page - Math.floor(maxVisible / 2));
                    let end = Math.min(meta.totalPages, start + maxVisible - 1);
                    start = Math.max(1, end - maxVisible + 1);

                    if (start > 1) {
                      pages.push(
                        <button key="1" onClick={() => handlePageChange(1)} className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all">1</button>
                      );
                      if (start > 2) {
                        pages.push(<span key="es" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>);
                      }
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg transition-all ${
                            i === page ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    if (end < meta.totalPages) {
                      if (end < meta.totalPages - 1) {
                        pages.push(<span key="ee" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>);
                      }
                      pages.push(
                        <button key={meta.totalPages} onClick={() => handlePageChange(meta.totalPages)} className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all">
                          {meta.totalPages}
                        </button>
                      );
                    }
                    return pages;
                  })()}
                </div>

                <button
                  onClick={handleNextPage}
                  disabled={page === meta.totalPages}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                    page === meta.totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Página siguiente"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePageChange(meta.totalPages)}
                  disabled={page === meta.totalPages}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                    page === meta.totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Última página"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición de cierre */}
      {editingClosure && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Ajustar Cierre de Caja</h3>
              <button onClick={closeEditModal} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Fecha</p>
                  <p className="font-semibold text-gray-900">{formatDate(editingClosure.fecha)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Vendedor</p>
                  <p className="font-semibold text-gray-900">{editingClosure.usuario?.nombreCompleto || '—'}</p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700">Valores Contados</h4>
                {[
                  { label: 'Efectivo Contado', key: 'efectivoContado' as const, ventas: editingClosure.efectivoVentas },
                  { label: 'Tarjeta Contado', key: 'tarjetaContado' as const, ventas: editingClosure.tarjetaVentas },
                  { label: 'Transferencias Contado', key: 'transferenciasContado' as const, ventas: editingClosure.transferenciasVentas },
                  { label: 'Nequi / Daviplata Contado', key: 'nequiContado' as const, ventas: editingClosure.nequiVentas },
                ].map((item) => (
                  <div key={item.key} className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">{item.label}</label>
                      <input
                        type="number"
                        value={editForm[item.key] ?? 0}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, [item.key]: Number(e.target.value) }))}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                      />
                    </div>
                    <div className="text-right min-w-[100px]">
                      <p className="text-[10px] text-gray-400">Ventas</p>
                      <p className="text-sm font-semibold text-emerald-600">{formatPrice(item.ventas)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xs text-blue-600">Total Contado (nuevo)</p>
                  <p className="text-lg font-bold text-blue-700">
                    {formatPrice((editForm.efectivoContado ?? editingClosure.efectivoContado) + (editForm.tarjetaContado ?? editingClosure.tarjetaContado) + (editForm.transferenciasContado ?? editingClosure.transferenciasContado) + (editForm.nequiContado ?? editingClosure.nequiContado))}
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-xs text-emerald-600">Diferencia (nueva)</p>
                  <p className="text-lg font-bold text-emerald-700">
                    {formatPrice(
                      ((editForm.efectivoContado ?? editingClosure.efectivoContado) + (editForm.tarjetaContado ?? editingClosure.tarjetaContado) + (editForm.transferenciasContado ?? editingClosure.transferenciasContado) + (editForm.nequiContado ?? editingClosure.nequiContado)) - editingClosure.totalVentas
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSavingEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSavingEdit && <Loader2 className="w-4 h-4 animate-spin" />}
                Guardar ajuste
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
